import { type CSSProperties, memo } from 'preact/compat';
import { useEffect, useLayoutEffect, useRef } from 'preact/hooks';

import { type Config, DotLottie, DotLottieWorker } from '@lottiefiles/dotlottie-web';

export interface LottieProps extends Omit<Config, 'canvas'> {
  className?: string;
  workerId?: string;

  isPlaying?: boolean;
  resetOnResume?: boolean;

  style?: CSSProperties;

  onPlay?: () => void;
  onPause?: () => void;
  onComplete?: () => void;
  onFrameChange?: ({ currentFrame }: { currentFrame: number }) => void;
}

type AnyLottie = DotLottieWorker | DotLottie;

/**
 * Check if OffscreenCanvas is supported
 */
const isPossibleToCreateWorkerLottie = () => {
  const canvas = document.createElement('canvas');
  return (
    'OffscreenCanvas' in window &&
    'transferControlToOffscreen' in canvas &&
    typeof canvas.transferControlToOffscreen === 'function'
  );
};

const createLottie = ({
  onPlay,
  onPause,
  onComplete,
  onFrameChange,
  ...config
}: Config & {
  workerId?: string;
  onPlay?: () => void;
  onPause?: () => void;
  onComplete?: () => void;
  onFrameChange?: ({ currentFrame }: { currentFrame: number }) => void;
}): AnyLottie => {
  const LottieCreator = isPossibleToCreateWorkerLottie() ? DotLottieWorker : DotLottie;

  const worker = new LottieCreator(config);

  if (onPlay) worker.addEventListener('play', onPlay);
  if (onPause) worker.addEventListener('pause', onPause);
  if (onComplete) worker.addEventListener('complete', onComplete);
  if (onFrameChange) worker.addEventListener('frame', onFrameChange);

  return worker;
};

export const Lottie = memo<LottieProps>(
  ({
    className,

    src,
    workerId: workerIdProp,

    loop = true,
    isPlaying = true,
    autoplay = isPlaying === true,
    resetOnResume = false,

    style,

    onPlay,
    onPause,
    onComplete,
    onFrameChange,

    ...props
  }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const lottieRef = useRef<AnyLottie | null>(null);

    useLayoutEffect(() => {
      if (!src || !canvasRef.current) return;

      // Ensure the canvas is in the DOM before transferring to Offscreen
      const canvas = canvasRef.current;
      if (!canvas.isConnected) return;

      // `createLottieWorker` has a side effect, so we have to force a single worker per canvas.
      // This is a workaround to prevent multiple workers from being created, which could happen
      // when react is on strict mode (development) or when the `src` prop changes.
      // It means that changing the props `src` will not cause re-render.
      if (canvas.hasAttribute('animation')) {
        return;
      }

      canvas.setAttribute('animation', 'true');

      const workerId =
        workerIdProp ??
        `${(Math.random() * 100).toFixed(0) + src.replace(/\W/gim, '_')}_`;

      const lottie = createLottie({
        canvas: canvasRef.current,
        src: new URL(src, import.meta.url).href,
        workerId,
        autoplay,
        loop,
        onPlay,
        onPause,
        onComplete,
        onFrameChange,

        renderConfig: { autoResize: true },

        ...props,
      });

      lottieRef.current = lottie;

      return () => {
        if (lottie instanceof DotLottieWorker) {
          lottie
            .destroy()
            .then(() => {
              // call _.worker.terminate() to avoid memory leak while navigating between pages
              (lottie as any)?._worker?.terminate?.();
            })
            .catch(() => {});
        } else {
          lottie.destroy();
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Control playback based on isPlaying and resetOnResume props
    useEffect(() => {
      if (!lottieRef.current || isPlaying === undefined) return;

      const lottie = lottieRef.current;

      if (isPlaying) {
        if (resetOnResume) lottie.setFrame(0);

        // TODO: Idk but it works only with 2 calling of play (???)
        setTimeout(() => {
          lottie.play();
          lottie.play();
        }, 0);
      } else if (resetOnResume) lottie.stop();
      else lottie.pause();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPlaying]);

    useEffect(() => {
      const lottie = lottieRef.current;

      if (!lottie) return;

      lottie.setLoop(loop);
    }, [loop]);

    useEffect(() => {
      const lottie = lottieRef.current;

      if (!lottie) return;

      if (onPlay) lottie.addEventListener('play', onPlay);
      if (onPause) lottie.addEventListener('pause', onPause);
      if (onComplete) lottie.addEventListener('complete', onComplete);
      if (onFrameChange) lottie.addEventListener('frame', onFrameChange);

      return () => {
        if (onPlay) lottie.removeEventListener('play', onPlay);
        if (onPause) lottie.removeEventListener('pause', onPause);
        if (onComplete) lottie.removeEventListener('complete', onComplete);
        if (onFrameChange) lottie.removeEventListener('frame', onFrameChange);
      };
    }, [onComplete, onFrameChange, onPause, onPlay]);

    return (
      <div className={className} style={style}>
        <canvas ref={canvasRef} style={{ width: '100%', height: '100%' }} />
      </div>
    );
  }
);

Lottie.displayName = 'Lottie';
